import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Loader2, Plus, Trash2, Send, Download, Copy, RefreshCw,
  CheckCircle, AlertCircle, Globe, Key, ToggleLeft, ToggleRight
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../AuthContext';
import { adminApi } from '../../services/api';
import { supabase } from '../../supabaseClient';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminWebhookSettingsPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);
  const [configs, setConfigs] = useState([]);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [createType, setCreateType] = useState('outgoing');
  const [createName, setCreateName] = useState('');
  const [createUrl, setCreateUrl] = useState('');
  const [creating, setCreating] = useState(false);

  // Delete dialog
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchConfigs = useCallback(async (t) => {
    try {
      setLoading(true);
      const data = await adminApi.getWebhookConfigs(t);
      setConfigs(data.configs || []);
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login'); return; }
    (async () => {
      const { data } = await supabase.auth.getSession();
      const t = data?.session?.access_token;
      if (!t) { navigate('/login'); return; }
      setToken(t);
      fetchConfigs(t);
    })();
  }, [isAuthenticated, navigate, fetchConfigs]);

  const handleCreate = async () => {
    if (createType === 'outgoing' && !createUrl.trim()) {
      toast({ title: 'URL required', description: 'Enter a webhook URL', variant: 'destructive' });
      return;
    }
    setCreating(true);
    try {
      const params = { config_type: createType, name: createName };
      if (createType === 'outgoing') params.url = createUrl;
      await adminApi.createWebhookConfig(params, token);
      toast({ title: 'Created', description: `${createType === 'outgoing' ? 'Outgoing' : 'Incoming'} webhook created.` });
      setCreateOpen(false);
      setCreateName('');
      setCreateUrl('');
      fetchConfigs(token);
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setCreating(false);
    }
  };

  const handleToggle = async (config) => {
    try {
      await adminApi.updateWebhookConfig(config.id, { is_active: String(!config.is_active) }, token);
      fetchConfigs(token);
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await adminApi.deleteWebhookConfig(deleteTarget.id, token);
      toast({ title: 'Deleted', description: 'Webhook configuration removed.' });
      setDeleteOpen(false);
      setDeleteTarget(null);
      fetchConfigs(token);
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const handleRegenerateSecret = async (config) => {
    try {
      await adminApi.regenerateWebhookSecret(config.id, token);
      toast({ title: 'Regenerated', description: 'New webhook secret generated.' });
      fetchConfigs(token);
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const handleTest = async (config) => {
    try {
      const result = await adminApi.testWebhook(config.id, token);
      if (result.success) {
        toast({ title: 'Success', description: result.message });
      } else {
        toast({ title: 'Failed', description: result.message, variant: 'destructive' });
      }
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({ title: 'Copied', description: 'Copied to clipboard.' });
  };

  const outgoing = configs.filter(c => c.config_type === 'outgoing');
  const incoming = configs.filter(c => c.config_type === 'incoming');

  if (loading) {
    return (
      <div style={{ backgroundColor: 'var(--bg-primary)', minHeight: '100vh', paddingTop: '80px' }}>
        <div className="flex items-center justify-center py-32">
          <Loader2 className="animate-spin" size={40} style={{ color: 'var(--olive-primary)' }} />
        </div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: 'var(--bg-primary)', minHeight: '100vh', paddingTop: '80px' }}>
      <div className="container max-w-4xl py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/admin">
            <Button variant="outline" size="icon" style={{ borderRadius: 0 }}>
              <ArrowLeft size={16} />
            </Button>
          </Link>
          <div className="flex-1">
            <h1 className="heading-1">Webhook Settings</h1>
            <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
              Sync bookings with your PMS system
            </p>
          </div>
          <Button
            onClick={() => setCreateOpen(true)}
            className="btn-primary flex items-center gap-2"
          >
            <Plus size={16} /> Add Webhook
          </Button>
        </div>

        {/* Outgoing Webhooks */}
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <Send size={20} style={{ color: 'var(--olive-primary)' }} />
            <h2 className="heading-2">Outgoing Webhooks</h2>
          </div>
          <p className="body-small mb-6" style={{ color: 'var(--text-secondary)' }}>
            When a booking is created, it will be sent as a POST request to these URLs so your PMS stays in sync.
          </p>

          {outgoing.length === 0 ? (
            <div
              className="p-8 text-center"
              style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-light)' }}
            >
              <Globe size={32} className="mx-auto mb-3" style={{ color: 'var(--text-light)' }} />
              <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                No outgoing webhooks configured. Add one to send bookings to your PMS.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {outgoing.map((wh) => (
                <div
                  key={wh.id}
                  className="p-6"
                  style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-light)' }}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="heading-3">{wh.name || 'Untitled Webhook'}</h3>
                        <span
                          className="text-xs px-2 py-0.5"
                          style={{
                            backgroundColor: wh.is_active ? 'var(--olive-light)' : 'var(--bg-primary)',
                            color: wh.is_active ? 'var(--olive-dark)' : 'var(--text-light)',
                            border: `1px solid ${wh.is_active ? 'var(--olive-primary)' : 'var(--border-light)'}`
                          }}
                        >
                          {wh.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <p className="body-small font-mono break-all" style={{ color: 'var(--text-secondary)' }}>
                        {wh.url}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        variant="outline"
                        size="icon"
                        title={wh.is_active ? 'Disable' : 'Enable'}
                        onClick={() => handleToggle(wh)}
                        style={{ borderRadius: 0 }}
                      >
                        {wh.is_active
                          ? <ToggleRight size={16} style={{ color: 'var(--olive-primary)' }} />
                          : <ToggleLeft size={16} />}
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        title="Test webhook"
                        onClick={() => handleTest(wh)}
                        style={{ borderRadius: 0 }}
                      >
                        <Send size={14} />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        title="Delete"
                        onClick={() => { setDeleteTarget(wh); setDeleteOpen(true); }}
                        style={{ borderRadius: 0, color: 'var(--error)' }}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Incoming Webhooks */}
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <Download size={20} style={{ color: 'var(--olive-primary)' }} />
            <h2 className="heading-2">Incoming Webhooks</h2>
          </div>
          <p className="body-small mb-2" style={{ color: 'var(--text-secondary)' }}>
            Generate a webhook URL and share it with your PMS. When the PMS sends booked dates to this URL,
            those dates will be blocked on the website calendar.
          </p>
          <div
            className="p-4 mb-6"
            style={{ backgroundColor: 'var(--olive-light)', border: '1px solid var(--olive-primary)' }}
          >
            <p className="body-small" style={{ color: 'var(--olive-dark)' }}>
              <strong>PMS Integration:</strong> Your PMS should send a POST request with JSON body:
              {' '}
              <code style={{ fontSize: '0.8rem' }}>
                {`{ "blocked_dates": [{ "room_id": "...", "check_in": "YYYY-MM-DD", "check_out": "YYYY-MM-DD" }] }`}
              </code>
            </p>
          </div>

          {incoming.length === 0 ? (
            <div
              className="p-8 text-center"
              style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-light)' }}
            >
              <Key size={32} className="mx-auto mb-3" style={{ color: 'var(--text-light)' }} />
              <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                No incoming webhooks configured. Generate one to receive blocked dates from your PMS.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {incoming.map((wh) => {
                const webhookUrl = `${API_URL}/api/webhooks/incoming/${wh.webhook_secret}`;
                return (
                  <div
                    key={wh.id}
                    className="p-6"
                    style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-light)' }}
                  >
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div className="flex items-center gap-2">
                        <h3 className="heading-3">{wh.name || 'Untitled Webhook'}</h3>
                        <span
                          className="text-xs px-2 py-0.5"
                          style={{
                            backgroundColor: wh.is_active ? 'var(--olive-light)' : 'var(--bg-primary)',
                            color: wh.is_active ? 'var(--olive-dark)' : 'var(--text-light)',
                            border: `1px solid ${wh.is_active ? 'var(--olive-primary)' : 'var(--border-light)'}`
                          }}
                        >
                          {wh.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="icon"
                          title={wh.is_active ? 'Disable' : 'Enable'}
                          onClick={() => handleToggle(wh)}
                          style={{ borderRadius: 0 }}
                        >
                          {wh.is_active
                            ? <ToggleRight size={16} style={{ color: 'var(--olive-primary)' }} />
                            : <ToggleLeft size={16} />}
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          title="Delete"
                          onClick={() => { setDeleteTarget(wh); setDeleteOpen(true); }}
                          style={{ borderRadius: 0, color: 'var(--error)' }}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>

                    {/* Webhook URL */}
                    <div className="mb-3">
                      <Label className="mb-1 block text-xs" style={{ color: 'var(--text-light)' }}>Webhook URL</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          value={webhookUrl}
                          readOnly
                          className="font-mono text-sm flex-1"
                          style={{ borderRadius: 0 }}
                        />
                        <Button
                          variant="outline"
                          size="icon"
                          title="Copy URL"
                          onClick={() => copyToClipboard(webhookUrl)}
                          style={{ borderRadius: 0 }}
                        >
                          <Copy size={14} />
                        </Button>
                      </div>
                    </div>

                    {/* Secret */}
                    <div>
                      <Label className="mb-1 block text-xs" style={{ color: 'var(--text-light)' }}>Secret Token</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          value={wh.webhook_secret}
                          readOnly
                          className="font-mono text-sm flex-1"
                          style={{ borderRadius: 0 }}
                        />
                        <Button
                          variant="outline"
                          size="icon"
                          title="Copy secret"
                          onClick={() => copyToClipboard(wh.webhook_secret)}
                          style={{ borderRadius: 0 }}
                        >
                          <Copy size={14} />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          title="Regenerate secret"
                          onClick={() => handleRegenerateSecret(wh)}
                          style={{ borderRadius: 0 }}
                        >
                          <RefreshCw size={14} />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Webhook</DialogTitle>
            <DialogDescription>
              Create an outgoing or incoming webhook configuration.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="mb-2 block">Type</Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setCreateType('outgoing')}
                  className="p-3 text-left text-sm transition-colors"
                  style={{
                    border: createType === 'outgoing' ? '2px solid var(--olive-primary)' : '1px solid var(--border-medium)',
                    backgroundColor: createType === 'outgoing' ? 'var(--olive-light)' : 'transparent',
                  }}
                >
                  <Send size={16} className="mb-1" style={{ color: createType === 'outgoing' ? 'var(--olive-primary)' : 'var(--text-secondary)' }} />
                  <div className="font-medium">Outgoing</div>
                  <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>Send bookings to PMS</div>
                </button>
                <button
                  type="button"
                  onClick={() => setCreateType('incoming')}
                  className="p-3 text-left text-sm transition-colors"
                  style={{
                    border: createType === 'incoming' ? '2px solid var(--olive-primary)' : '1px solid var(--border-medium)',
                    backgroundColor: createType === 'incoming' ? 'var(--olive-light)' : 'transparent',
                  }}
                >
                  <Download size={16} className="mb-1" style={{ color: createType === 'incoming' ? 'var(--olive-primary)' : 'var(--text-secondary)' }} />
                  <div className="font-medium">Incoming</div>
                  <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>Receive blocked dates</div>
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor="webhookName">Name (optional)</Label>
              <Input
                id="webhookName"
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                placeholder="e.g. Main PMS"
                style={{ borderRadius: 0 }}
              />
            </div>
            {createType === 'outgoing' && (
              <div>
                <Label htmlFor="webhookUrl">Webhook URL</Label>
                <Input
                  id="webhookUrl"
                  value={createUrl}
                  onChange={(e) => setCreateUrl(e.target.value)}
                  placeholder="https://your-pms.com/api/webhook"
                  style={{ borderRadius: 0 }}
                />
              </div>
            )}
            {createType === 'incoming' && (
              <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                A secure webhook URL and secret will be generated automatically. Share these with your PMS provider.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} style={{ borderRadius: 0 }}>
              Cancel
            </Button>
            <Button className="btn-primary" onClick={handleCreate} disabled={creating}>
              {creating ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
              Create Webhook
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Webhook</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteTarget?.name || 'this webhook'}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)} style={{ borderRadius: 0 }}>
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              style={{ borderRadius: 0, backgroundColor: 'var(--error)', color: '#fff' }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminWebhookSettingsPage;
